/* eslint-disable react/prop-types */
/* eslint-disable import/order */
// FullCalendar v5: the React entry MUST be imported before any plugin, or the
// plugin throws "import the top-level fullcalendar lib" at module load.
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React from 'react'
import styled from 'styled-components'

import { fetchMyAtHomeSlots } from 'api/atHome'
import { useIsMobile } from 'app/hooks/useIsMobile'
import { DayHeaderContent } from 'app/pages/Class/Inventory/components/Schedule/components/DayHeaderContent'
import { categoryColorMap } from 'app/pages/Class/Inventory/components/Schedule/constants'
import { Container } from 'app/pages/Class/Inventory/components/Schedule/styles'

import { fmtMoney, fmtTime } from '../format'

/**
 * Week calendar of a teacher's At Home slots — same FullCalendar day-grid,
 * day headers and card language as the Booking (inventory) page, so the two
 * feel like one product. Open slots use the category colour; booked ones are
 * marked and show the child.
 */
const colour = (name) => categoryColorMap[name] || categoryColorMap.fallback

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 192px;
  margin: 6px auto;
  padding: 8px 8px 0;
  border-radius: 8px;
  background: ${(p) => colour(p.category).secondary};
  border: 1px solid ${(p) => colour(p.category).primary};
  color: ${(p) => colour(p.category).text};
  display: flex;
  flex-direction: column;
  white-space: normal;
  cursor: default;
  transition: box-shadow 0.3s ease;
  &:hover {
    box-shadow: 10px 10px 33px -16px rgba(0, 0, 0, 0.75);
  }
`
const Time = styled.div`
  font-size: 14px;
  font-weight: 700;
`
const Meta = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  opacity: 0.9;
`
const Name = styled.div`
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  margin-top: 2px;
`
const Line = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.7);
`
const Badge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: ${(p) => (p.booked ? p.primary : 'rgba(255,255,255,0.9)')};
  color: ${(p) => (p.booked ? 'white' : 'rgba(0,0,0,0.8)')};
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 600;
`
const Footer = styled.div`
  margin: 8px -8px 0;
  height: 22px;
  border-radius: 0 0 7px 7px;
  background: ${(p) => colour(p.category).primary};
  color: white;
  font-weight: 700;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

const SlotCard = ({ event }) => {
  const s = event.extendedProps
  const booked = s.status === 'booked'
  const c = colour(s.categoryName)
  return (
    <Card category={s.categoryName} title={s.notes || ''}>
      <Badge booked={booked} primary={c.primary}>
        {booked ? 'Booked' : 'Open'}
      </Badge>
      <Time>
        {fmtTime(s.start_time)} – {fmtTime(s.endTime)}
      </Time>
      <Meta>
        {s.kind === 'trial' ? 'Trial' : 'Regular'} · {fmtMoney(s.price)}
      </Meta>
      <Name>{s.categoryName}</Name>
      <Line>{s.teacherName}</Line>
      <Line>{s.grade}</Line>
      {booked && s.booking?.child?.name && (
        <Line>For {s.booking.child.name}</Line>
      )}
      <Footer category={s.categoryName}>{s.apartmentName}</Footer>
    </Card>
  )
}

// A filter bar above the week grid (owner, 2026-10-04). Options come from
// the slots the calendar has loaded, so they only ever list real values;
// the Teacher filter appears only when more than one teacher is present
// (i.e. for admins — a teacher sees just their own slots anyway).
const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  align-items: center;
  padding: 0 0 12px;
  font-size: 13px;
`
const Select = styled.select`
  width: auto;
  flex: none;
  font: inherit;
  font-size: 13px;
  padding: 5px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #fff;
  color: ${(p) => (p.$active ? '#1f1b16' : '#6f675e')};
  border-color: ${(p) => (p.$active ? '#e4572e' : '#e0e0e0')};
`
const ClearBtn = styled.button`
  font: inherit;
  font-size: 13px;
  color: #e4572e;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
`

const FILTERS = [
  { key: 'teacherName', label: 'Teacher' },
  { key: 'categoryName', label: 'Category' },
  { key: 'apartmentName', label: 'Apartment' },
  {
    key: 'kind',
    label: 'Type',
    render: (v) => (v === 'trial' ? 'Trial' : 'Regular')
  },
  {
    key: 'status',
    label: 'Status',
    render: (v) => (v === 'open' ? 'Open' : 'Booked')
  }
]

const SlotsCalendar = ({ status = 'all' }) => {
  const isMobile = useIsMobile()
  const [filters, setFilters] = React.useState({})
  // Every non-cancelled slot the calendar has seen, for the dropdowns.
  const [seen, setSeen] = React.useState([])
  const calRef = React.useRef(null)

  const options = React.useMemo(() => {
    const out = {}
    FILTERS.forEach((f) => {
      out[f.key] = [...new Set(seen.map((s) => s[f.key]).filter(Boolean))].sort(
        (a, b) => String(a).localeCompare(String(b))
      )
    })
    return out
  }, [seen])
  const active = Object.values(filters).some(Boolean)

  // FullCalendar refetches whenever its `events` prop changes identity, and
  // the fetch itself updates `seen`, so the source must be stable: filters
  // are read through a ref, `seen` is only set when the slot set changes,
  // and a filter change asks the calendar to refetch explicitly.
  const filtersRef = React.useRef(filters)
  filtersRef.current = filters
  const seenSig = React.useRef('')
  React.useEffect(() => {
    calRef.current?.getApi?.().refetchEvents()
  }, [filters])

  const events = React.useCallback(
    async (range, success, failure) => {
      try {
        const from = dayjs(range.start).format('YYYY-MM-DD')
        const to = dayjs(range.end).format('YYYY-MM-DD')
        const rows = await fetchMyAtHomeSlots(status, from)
        // withdrawn (cancelled) slots are gone as far as a teacher is concerned
        const live = rows.filter(
          (s) => s.status !== 'cancelled' && s.date >= from && s.date < to
        )
        const sig = live.map((s) => `${s.uuid}:${s.status}`).join('|')
        if (sig !== seenSig.current) {
          seenSig.current = sig
          setSeen(live)
        }
        const f = filtersRef.current
        success(
          live
            .filter((s) =>
              FILTERS.every((x) => !f[x.key] || s[x.key] === f[x.key])
            )
            .map((s) => ({
              id: s.uuid,
              title: s.categoryName,
              start: `${s.date}T${s.start_time}`,
              end: `${s.date}T${s.endTime}`,
              extendedProps: s
            }))
        )
      } catch (e) {
        failure(e)
      }
    },
    [status]
  )

  return (
    <Container style={{ width: '100%' }}>
      <FilterBar>
        {FILTERS.filter(
          (f) => f.key !== 'teacherName' || options.teacherName.length > 1
        ).map((f) => (
          <Select
            key={f.key}
            aria-label={`Filter ${f.label}`}
            value={filters[f.key] || ''}
            $active={Boolean(filters[f.key])}
            onChange={(e) =>
              setFilters((cur) => ({ ...cur, [f.key]: e.target.value }))
            }
          >
            <option value="">{f.label}: all</option>
            {options[f.key].map((v) => (
              <option key={v} value={v}>
                {f.render ? f.render(v) : v}
              </option>
            ))}
          </Select>
        ))}
        {active && (
          <ClearBtn onClick={() => setFilters({})}>Clear filters</ClearBtn>
        )}
      </FilterBar>
      {/* FullCalendar reads initialView once; the hook may resolve after the
          first render, so remount on change instead of trusting the first value. */}
      <FullCalendar
        ref={calRef}
        key={isMobile ? 'day' : 'week'}
        plugins={[dayGridPlugin]}
        initialView={isMobile ? 'dayGridDay' : 'dayGridWeek'}
        firstDay="1"
        headerToolbar={{ left: 'prev next title', center: '', right: 'today' }}
        titleFormat={{ day: 'numeric', month: 'short' }}
        dayHeaderContent={DayHeaderContent}
        eventContent={(props) => <SlotCard {...props} />}
        eventDisplay="block"
        eventBackgroundColor="transparent"
        eventBorderColor="transparent"
        displayEventTime={false}
        height="auto"
        events={events}
      />
    </Container>
  )
}

SlotsCalendar.propTypes = { status: PropTypes.string }

export default SlotsCalendar
